import { clamp } from '../../lib/format.ts'
import type { Tone } from './Metric.tsx'

const TONE_FILL: Record<Tone, string> = {
  neutral: 'bg-line-strong',
  accent: 'bg-accent',
  ok: 'bg-ok',
  warn: 'bg-warn',
  crit: 'bg-crit',
}

export interface Threshold {
  /** Percentage along the track, 0–100. */
  at: number
  label: string
}

interface MeterProps {
  /** 0–100. Values above 100 clamp but keep their real reading in `valueText`. */
  pct: number
  tone: Tone
  /** Screen-reader description of the reading, e.g. "6.104 de 50.000 (12,2%)". */
  valueText: string
  label: string
  thresholds?: readonly Threshold[]
  /** Taller track for the panel-level meter; thin for inline table cells. */
  size?: 'lg' | 'sm'
  className?: string
}

/**
 * A reading against its ceiling.
 *
 * Queue depth in isolation is meaningless — 12% only means something relative to
 * the limit, so this never renders a bare number: the track *is* the capacity and
 * the marks are the thresholds an operator actually reacts to.
 */
export function Meter({
  pct,
  tone,
  valueText,
  label,
  thresholds = [],
  size = 'lg',
  className = '',
}: MeterProps) {
  const width = clamp(pct, 0, 100)

  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuenow={Math.round(pct * 10) / 10}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={valueText}
      className={`relative w-full overflow-hidden bg-inset ${size === 'lg' ? 'h-2.5' : 'h-1.5'} ${className}`}
    >
      {/* No easing on the fill: it tracks a value that changes every 2s, and a
          transition there is a bar that never stops moving. */}
      <div
        className={`h-full ${TONE_FILL[tone]}`}
        style={{ width: `${width}%` }}
      />
      {thresholds.map((threshold) => (
        <span
          key={threshold.at}
          aria-hidden="true"
          title={threshold.label}
          className="absolute top-0 h-full w-px bg-canvas"
          style={{ left: `${clamp(threshold.at, 0, 100)}%` }}
        />
      ))}
    </div>
  )
}

/** Zone a utilisation reading falls into. Shared by the queue and quota meters. */
export function utilisationTone(pct: number, warnAt = 60, critAt = 85): Tone {
  if (pct >= critAt) return 'crit'
  if (pct >= warnAt) return 'warn'
  return 'ok'
}
